import { Dispatch } from "react";
import { downloadZip } from "client-zip";
import { ArchiveManifestInterface } from "../../middleware/interfaces/server/archive-manifest-interface";
import { StartBrowserDownload } from "../ui/methods";

/**
 * Bundles a folder or project download in the browser.
 *
 * The server used to zip these itself, which meant pulling every object out of
 * Wasabi, writing a zip to local disk and pushing it back up as a staged copy --
 * three passes over the data, and Wasabi's 90-day minimum charge on every
 * archive. Now the server only says what belongs in the download and the bytes
 * go straight from Wasabi to the user.
 */

/**
 * Yields each file as client-zip consumes it.
 *
 * A generator rather than an array so only one file is ever in flight: each
 * response is streamed into the archive and released before the next fetch
 * starts. Building the array up front would hold the whole project in memory,
 * which is the thing this change exists to avoid.
 */
async function* StreamArchiveFiles(
  archiveManifest: ArchiveManifestInterface,
  dispatch: Dispatch<any>
) {
  // Emitted first so the structure exists even where a folder holds nothing.
  // client-zip writes an entry with no input as a directory.
  for (const archiveFolder of archiveManifest.archiveFolders) {
    yield { name: archiveFolder };
  }

  const archiveFiles = archiveManifest.archiveFiles;
  const totalBytes = archiveFiles.reduce(
    (total, archiveFile) => total + archiveFile.size,
    0
  );
  let downloadedBytes = 0;

  for (const archiveFile of archiveFiles) {
    const fileResponse = await fetch(archiveFile.url);

    if (!fileResponse.ok) {
      throw new Error(`Failed to download ${archiveFile.path}`);
    }

    yield { name: archiveFile.path, input: fileResponse };

    downloadedBytes += archiveFile.size;

    // Reported per file rather than per chunk: the file is only counted once
    // client-zip has finished pulling it, so the figure never runs ahead.
    dispatch({
      type: "ARCHIVE_PROGRESS",
      payload: Math.min(99, Math.round((downloadedBytes / totalBytes) * 100)),
    });
  }
}

/**
 * Fetch every file in a manifest and save them as one archive.
 *
 * Where the browser supports it the zip is written straight to the file the
 * user picks, so memory stays flat no matter how large the project is. Firefox
 * and Safari have no such API, so there the finished archive is buffered as a
 * Blob first.
 */
export const DownloadArchiveFromManifest = async (
  archiveManifest: ArchiveManifestInterface,
  dispatch: Dispatch<any>
) => {
  let archiveHandle: any = null;
  let pickerCancelled = false;

  // Asked before a single byte is fetched, so cancelling costs nothing.
  if ("showSaveFilePicker" in window) {
    try {
      archiveHandle = await (window as any).showSaveFilePicker({
        suggestedName: archiveManifest.archiveFileName,
        types: [
          { description: "Archive", accept: { "application/zip": [".zip"] } },
        ],
      });
    } catch (error: any) {
      // Closing the dialog is a decision, not a failure.
      pickerCancelled = error?.name === "AbortError";

      // Anything else leaves archiveHandle null and falls through to the Blob
      // path. The likely cause is the browser refusing a file picker outside a
      // user gesture: the click that started this was spent waiting on the
      // manifest, and transient activation does not survive a slow request.
    }
  }

  if (pickerCancelled) {
    return;
  }

  dispatch({ type: "ARCHIVE_PROGRESS", payload: 0 });

  const archiveResponse = downloadZip(
    StreamArchiveFiles(archiveManifest, dispatch)
  );

  if (archiveHandle) {
    // Straight to disk. A failure part way through aborts the writable, so a
    // truncated archive is never committed.
    await archiveResponse.body!.pipeTo(await archiveHandle.createWritable());
    return;
  }

  const archiveBlob = await archiveResponse.blob();
  const archiveUrl = window.URL.createObjectURL(archiveBlob);

  StartBrowserDownload(archiveUrl, archiveManifest.archiveFileName);
  window.URL.revokeObjectURL(archiveUrl);
};
